import React, { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { uid } from '@/lib/helpers';
import { Loader2, ArrowLeftRight } from 'lucide-react';

/**
 * Bulk move selected garments to a destination store using proper transfer records.
 * Only AVAILABLE items are moved; sold / in-transit items are skipped.
 * props: open, onOpenChange, items (selected garment objects), onDone()
 */
export default function BulkMoveStoreDialog({ open, onOpenChange, items = [], onDone }) {
  const { stores } = useApp();
  const qc = useQueryClient();
  const [dest, setDest] = useState('');
  const [saving, setSaving] = useState(false);

  const available = useMemo(() => items.filter((g) => g.status === 'AVAILABLE'), [items]);
  const skipped = items.length - available.length;
  // group available by source store
  const groups = useMemo(() => {
    const m = {};
    for (const g of available) (m[g.store_id] = m[g.store_id] || []).push(g.barcode);
    return m;
  }, [available]);
  const movable = useMemo(() => (dest ? available.filter((g) => g.store_id !== dest).length : available.length), [available, dest]);
  const storeName = (id) => stores.find((s) => s.id === id)?.name || id;

  const move = async () => {
    if (!dest) { toast.error('Choose a destination store.'); return; }
    const jobs = Object.entries(groups).filter(([from]) => from !== dest);
    if (jobs.length === 0) { toast.error('Selected items are already in that store (or none are available to move).'); return; }
    setSaving(true);
    let moved = 0;
    try {
      for (const [from, barcodes] of jobs) {
        const r = await api.post('/transfers', { from_store: from, to_store: dest, barcodes, immediate: true, client_txn_id: uid() });
        moved += r.data.data.count || 0;
      }
      toast.success(`${moved} item(s) moved to ${storeName(dest)}${skipped ? ` · ${skipped} skipped (not available)` : ''}`);
      qc.invalidateQueries({ queryKey: ['garments'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowLeftRight className="h-5 w-5 text-primary" /> Move to Store</DialogTitle>
          <DialogDescription>
            {available.length} available item{available.length === 1 ? '' : 's'} selected{skipped ? ` (${skipped} sold/in-transit will be skipped)` : ''}. A transfer record is created for each source store.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Destination store</Label>
            <Select value={dest} onValueChange={setDest}>
              <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="movestore-dest"><SelectValue placeholder="Choose store" /></SelectTrigger>
              <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm" data-testid="movestore-summary">
            {Object.entries(groups).length === 0 ? <p className="text-muted-foreground">No available items to move.</p> :
              Object.entries(groups).map(([from, bcs]) => (
                <div key={from} className="flex items-center justify-between py-0.5">
                  <span className="text-muted-foreground">{storeName(from)}</span>
                  <span className="font-medium">{bcs.length} item{bcs.length === 1 ? '' : 's'}{dest && from === dest ? ' · already here' : ''}</span>
                </div>
              ))}
            {dest && <div className="mt-2 border-t border-border pt-2 font-medium">{movable} will move → {storeName(dest)}</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" className="rounded-lg" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="rounded-lg" onClick={move} disabled={saving || !dest || movable === 0} data-testid="movestore-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Move ${movable} item${movable === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
